/**
 * 유틸리티 헬퍼 함수
 */

/**
 * 날짜를 상대 시간으로 포맷팅
 * 
 * @param dateString - 날짜 문자열 (ISO 형식)
 * @returns 상대 시간 문자열 (예: "2시간 전", "3일 전")
 */
export const formatRelativeTime = (dateString: string | null | undefined): string => {
  if (!dateString) {
    return '로그인 기록 없음';
  }

  const now = new Date();
  const date = new Date(dateString);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) {
    return '방금 전';
  }
  if (diffMins < 60) {
    return `${diffMins}분 전`;
  }
  if (diffHours < 24) {
    return `${diffHours}시간 전`;
  }
  if (diffDays < 7) {
    return `${diffDays}일 전`;
  }
  
  // 7일 이상 경과한 경우 절대 시간 표시
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * 권한 레벨에 따른 배지 색상 클래스 반환
 * 
 * @param roleGroupName - 권한 그룹 이름
 * @returns 배지 색상 클래스명
 */
export const getRoleBadgeColor = (roleGroupName: string | undefined): string => {
  if (!roleGroupName) {
    return 'badge-gray';
  }

  const role = roleGroupName.toLowerCase();
  
  if (role.includes('super') || role.includes('슈퍼')) {
    return 'badge-super-admin';
  }
  if (role.includes('admin') || role.includes('관리자')) {
    return 'badge-admin';
  }
  if (role.includes('site') || role.includes('manager') || role.includes('현장')) {
    return 'badge-site-manager';
  }
  if (role.includes('viewer') || role.includes('조회')) {
    return 'badge-viewer';
  }
  
  return 'badge-gray';
};

/**
 * 권한 레벨에 따른 한글 라벨 반환
 * 
 * @param roleGroupName - 권한 그룹 이름
 * @returns 한글 권한 레벨 라벨
 */
export const getRoleLabel = (roleGroupName: string | undefined): string => {
  if (!roleGroupName) {
    return '운영자';
  }

  const role = roleGroupName.toLowerCase();
  
  if (role.includes('super') || role.includes('슈퍼')) {
    return '슈퍼관리자';
  }
  if (role.includes('admin') || role.includes('관리자')) {
    return '관리자';
  }
  if (role.includes('site') || role.includes('manager') || role.includes('현장')) {
    return '현장관리자';
  }
  if (role.includes('viewer') || role.includes('조회')) {
    return '조회전용';
  }
  
  return roleGroupName;
};

/**
 * IP 주소 마스킹 처리 (보안 강화 옵션)
 * 
 * @param ipAddress - IP 주소 문자열
 * @param maskLastOctet - 마지막 옥텟 마스킹 여부 (기본: false)
 * @returns 마스킹된 IP 주소 또는 원본 IP 주소
 */
export const maskIPAddress = (ipAddress: string | null | undefined, maskLastOctet: boolean = false): string => {
  if (!ipAddress) {
    return 'IP 정보 없음';
  }

  if (maskLastOctet) {
    // 마지막 옥텟 마스킹 (예: 192.168.1.xxx)
    const parts = ipAddress.split('.');
    if (parts.length === 4) {
      return `${parts[0]}.${parts[1]}.${parts[2]}.xxx`;
    }
  }

  return ipAddress;
};

